import { IUnityLoader } from "./Types/IUnityLoader";
import { Unity, useUnityContext } from "react-unity-webgl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCompressedTextureSupport } from "../../../../Utils/Hooks/useCompressedTextureSupport";
import { useSearchParams } from "react-router-dom";
import { UnityConfig } from "react-unity-webgl/distribution/types/unity-config";
import {
	FxIdDomainSettingsUnityOptionsDistributionForceCompressionOption,
	FxIdDomainSettingsUnityOptionsDistributionWebGlContextOptionsPowerPreference,
	FxIdDomainSettingsUnityOptionsDistributionWebGlContextOpts
} from "@app/Api/gen";
import { WebGLContextAttributes } from "react-unity-webgl/distribution/types/webgl-context-attributes";

function mapToWebGLContextAttributes(
	source: FxIdDomainSettingsUnityOptionsDistributionWebGlContextOpts
): WebGLContextAttributes {
	return {
		alpha: source.Alpha ?? undefined, // convert null to undefined
		antialias: source.Antialias ?? undefined,
		depth: source.Depth ?? undefined,
		failIfMajorPerformanceCaveat: source.FailIfMajorPerformanceCaveat ?? undefined,
		powerPreference: mapPowerPreference(source.PowerPreference),
		premultipliedAlpha: source.PremultipliedAlpha ?? undefined,
		preserveDrawingBuffer: source.PreserveDrawingBuffer ?? undefined,
		stencil: source.Stencil ?? undefined,
		desynchronized: source.Desynchronized ?? undefined,
		xrCompatible: source.XrCompatible ?? undefined
	};
}

function mapPowerPreference(
	source: FxIdDomainSettingsUnityOptionsDistributionWebGlContextOptionsPowerPreference | null | undefined
): 0 | 1 | 2 | undefined {
	switch (source) {
		case FxIdDomainSettingsUnityOptionsDistributionWebGlContextOptionsPowerPreference.Low:
			return 0;
		case FxIdDomainSettingsUnityOptionsDistributionWebGlContextOptionsPowerPreference.Medium:
			return 1;
		case FxIdDomainSettingsUnityOptionsDistributionWebGlContextOptionsPowerPreference.High:
			return 2;
		default:
			return undefined; // Ignore unknown or null values
	}
}

export const UnityLoader = (props: IUnityLoader) => {
	log.info("Render UnityLoader...");
	const [searchParams] = useSearchParams();

	const { buildConfig, onGameLoaded, onGameLoadingProgress, unityRef } = props;
	const unityConfig = useMemo(() => buildConfig.UnityGameConfig, [buildConfig.UnityGameConfig]);

	const webGlComponentOptions = buildConfig.PublicWebClientConfig?.Unity?.Distribution?.Component;
	const fixedDevicePixelRatio = webGlComponentOptions?.DevicePixelRatio;
	const automaticDevicePixelRatio = webGlComponentOptions?.AutomaticDevicePixelRatio;
	const matchWebGlSize = webGlComponentOptions?.MatchWebGLToCanvasSize;

	// We'll use a state to store the device pixel ratio.
	const [devicePixelRatio, setDevicePixelRatio] = useState(window.devicePixelRatio);

	const companyName = buildConfig.CompanyName;
	const productName = buildConfig.GameSystemName;

	useEffect(() => {
		log.info("Company name: %s. Product name: %s", companyName, productName);
	}, [companyName, productName]);

	// TODO: Убрать после релиза 2024-11-01 - но проверить что настройки в конфиге на бете/стаже/проде поставлены как надо (отключены текстуры)
	const isMergeArena = buildConfig.GameSystemName === "mergearena" || buildConfig.GameSystemName === "mergearenaint";

	const compressedTextureSupported = useCompressedTextureSupport() && !isMergeArena;
	let unityBaseBuildUrl = unityConfig!.BaseBuildUrl;

	const unityFilesCompression = buildConfig.PublicWebClientConfig.Unity?.Distribution?.ForceCompressionOption;

	let disableBrotli = false;

	if (unityFilesCompression === FxIdDomainSettingsUnityOptionsDistributionForceCompressionOption.Disabled) {
		disableBrotli = true;
	}

	if (import.meta.env.VITE_APP_BUILD_MODE == "standalone") {
		unityBaseBuildUrl = new URL("vendor_app", window.location.toString()).toString();
		disableBrotli = true;
	}

	const brotliExtension = disableBrotli ? "" : ".br";

	const desktopDataFile = buildConfig.PublicWebClientConfig.Unity?.Distribution?.DataFileName ?? "WebGL_Build.data";
	const mobileDataFile = buildConfig.PublicWebClientConfig.Unity?.Distribution?.DataFileName ?? "WebGL_Mobile.data";

	const dataFile = useMemo(
		() =>
			compressedTextureSupported && mobileDataFile != null
				? `${desktopDataFile}${brotliExtension}`
				: `${mobileDataFile}${brotliExtension}`,
		[buildConfig.GameSystemName, compressedTextureSupported]
	);

	const streamingAssetsUrl =
		buildConfig.PublicWebClientConfig.Unity?.Distribution?.StreamingAssetsUrl ?? "StreamingAssets";
	const streamingAssetsMobileUrl =
		buildConfig.PublicWebClientConfig.Unity?.Distribution?.StreamingAssetsUrl ?? "StreamingAssetsMobile";
	const unityBaseUrl = unityConfig!.BaseBuildUrl.split("/Build")[0];

	const streamingAssets = useMemo(() => {
		const stAssetsUrl =
			compressedTextureSupported && streamingAssetsMobileUrl != null
				? streamingAssetsMobileUrl
				: streamingAssetsUrl;

		if (stAssetsUrl.startsWith("http")) {
			return stAssetsUrl;
		}

		return `${unityBaseUrl}/${stAssetsUrl}`;
	}, [buildConfig.GameSystemName, compressedTextureSupported]);

	const cacheableExtensions = [".data", ".bundle", ".br", ".wasm", ".gz"];

	const isCacheableUrl = (url: string) => {
		return cacheableExtensions.some((ext) => url.includes(ext));
	};

	const cacheControl = useCallback((url: string) => {
		if (isCacheableUrl(url)) {
			log.info("Will cache url " + url);
			return "must-revalidate";
		}
		return "no-store";
	}, []);

	const loaderFileName =
		buildConfig.PublicWebClientConfig.Unity?.Distribution?.LoaderFileName ?? "WebGL_Build.loader.js";
	const codeFileName = `${buildConfig.PublicWebClientConfig.Unity?.Distribution?.CodeFileName ?? "WebGL_Build.wasm"}${brotliExtension}`;
	const frameworkFileName = `${
		buildConfig.PublicWebClientConfig?.Unity?.Distribution?.FrameworkFileName ?? "WebGL_Build.framework.js"
	}${brotliExtension}`;
	const symbolsFileName = `${
		buildConfig.PublicWebClientConfig?.Unity?.Distribution?.SymbolsFileName ?? "WebGL_Build.symbols.json"
	}${brotliExtension}`;

	const memoryFileName = buildConfig.PublicWebClientConfig?.Unity?.Distribution?.MemoryFileName;

	const webGlContextOptions = buildConfig.PublicWebClientConfig?.Unity?.Distribution?.WebGlContextOpts;

	const unityContextConfig: UnityConfig = {
		loaderUrl: `${unityBaseBuildUrl}/${loaderFileName}`,
		dataUrl: `${unityBaseBuildUrl}/${dataFile}`,
		codeUrl: `${unityBaseBuildUrl}/${codeFileName}`,
		frameworkUrl: `${unityBaseBuildUrl}/${frameworkFileName}`,
		symbolsUrl: `${unityBaseBuildUrl}/${symbolsFileName}`,
		memoryUrl: memoryFileName != null ? `${unityBaseBuildUrl}/${memoryFileName}` : undefined,
		// NOTE: стриминг ассеты по прежнему лежат где-то на цдн-е
		streamingAssetsUrl: streamingAssets,
		companyName: companyName,
		productName: productName,
		productVersion: unityBaseBuildUrl,
		webglContextAttributes:
			webGlContextOptions != null ? mapToWebGLContextAttributes(webGlContextOptions) : { powerPreference: 2 },
		cacheControl
	};

	const { unityProvider, loadingProgression, isLoaded, requestFullscreen, requestPointerLock } =
		useUnityContext(unityContextConfig);

	useEffect(() => {
		if (buildConfig.PublicWebClientConfig.Unity?.Distribution?.RequestPointerLocking === true) {
			document.addEventListener("click", requestPointerLock);
		}
		return () => {
			if (buildConfig.PublicWebClientConfig.Unity?.Distribution?.RequestPointerLocking === true) {
				document.removeEventListener("click", requestPointerLock);
			}
		};
	}, [requestPointerLock]);

	useEffect(() => {
		if (unityRef) {
			unityRef.current = { requestFullscreen };
		}
	}, [requestFullscreen, unityRef]);

	useEffect(() => {
		if (onGameLoaded && isLoaded) onGameLoaded();
	}, [isLoaded]);

	useEffect(() => {
		if (!onGameLoadingProgress) return;
		onGameLoadingProgress(loadingProgression);
	}, [loadingProgression]);

	useEffect(
		function () {
			if (!automaticDevicePixelRatio) {
				// If automaticDevicePixelRatio is false, do nothing.
				return;
			}

			// Function to update the device pixel ratio of the Unity Application.
			const updateDevicePixelRatio = function () {
				setDevicePixelRatio(window.devicePixelRatio);
			};

			// Media matcher to watch for changes in the device pixel ratio.
			const mediaMatcher = window.matchMedia(`screen and (resolution: ${devicePixelRatio}dppx)`);

			// Add event listener to update the device pixel ratio when it changes.
			mediaMatcher.addEventListener("change", updateDevicePixelRatio);

			return function () {
				// Remove the event listener when the component unmounts.
				mediaMatcher.removeEventListener("change", updateDevicePixelRatio);
			};
		},
		[devicePixelRatio, automaticDevicePixelRatio] // Include automaticDevicePixelRatio in dependencies
	);

	// if (searchParams.get("debug") === "1") {
	// 	return (
	// 		<Unity
	// 			devicePixelRatio={1}
	// 			matchWebGLToCanvasSize={false}
	// 			unityProvider={unityProvider}
	// 			style={{ width: props.dimensions.width }}
	// 		/>
	// 	);
	// }

	const componentDevicePixelRatio = fixedDevicePixelRatio
		? fixedDevicePixelRatio
		: automaticDevicePixelRatio
			? devicePixelRatio
			: undefined;

	return (
		<Unity
			unityProvider={unityProvider}
			devicePixelRatio={componentDevicePixelRatio}
			matchWebGLToCanvasSize={matchWebGlSize === true ? true : undefined}
			style={{
				width: props.dimensions.width
				// position: "absolute",
				// top: "0px",
				// left: "0px",
				// width: "100%",
				// height: "100%"
			}}
		/>
	);
};
